import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Modal } from "@/shared/ui/modal";

import { useSetUserRoles } from "../api/use-set-user-roles";
import { ROLES } from "../lib/user-form-schema";

type AdminUser = components["schemas"]["AdminUser"];

interface SetRolesDialogProps {
  user: AdminUser;
  onClose: () => void;
}

export function SetRolesDialog({ user, onClose }: SetRolesDialogProps): JSX.Element {
  const { t } = useTranslation("users");
  const setRoles = useSetUserRoles();
  const [selected, setSelected] = useState<string[]>(user.roles);

  function toggle(value: string): void {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  }

  function onConfirm(): void {
    setRoles.mutate(
      { id: user.id, roleNames: selected },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("set_roles.success"));
        },
        onError: (err) => toast.error(apiErrorMessage(err)),
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("set_roles.title", { name: user.display_name })}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={setRoles.isPending}
            data-testid="set-roles-cancel"
          >
            {t("action.cancel")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={setRoles.isPending || selected.length === 0}
            data-testid="set-roles-confirm"
          >
            {t("action.save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2" data-testid="set-roles-list">
        {ROLES.map((role) => (
          <label key={role.value} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selected.includes(role.value)}
              onChange={() => toggle(role.value)}
              disabled={setRoles.isPending}
              data-testid={`set-roles-${role.value}`}
            />
            {t(role.labelKey)}
          </label>
        ))}
      </div>
    </Modal>
  );
}
